'use strict';
import Helpers from './_helpers';
import Click from './lib/click';
import Marketo from './lib/marketo';
import Form from './lib/form';
import YouTube from './lib/youtube';
import Wistia from './lib/wistia';
/**
 * Main RDDL
 *
 * Defines and attaches events for custom dataLayer payloads
 */
export default class RDDL {
    constructor() {
        /**
         * Helpers
         *
         * @type Helpers
         */
        this.helpers = new Helpers;
        /**
         * User metadata
         *
         * @type User_Interface
         */
        this.user = {
            client_id: '',
            id: '',
            is_logged_in: false,
            munchkin_id: '',
            segment_id: '',
        };
        /**
         * YouTube players array
         *
         * @type Array<object>
         */
        this.yt = [];
        window.dataLayer = window.dataLayer || [];
    }
    /**
     * Init
     *
     * @param string            client_id
     * @param Config_Interface  user_config
     */
    init(client_id, user_config) {
        // Merge user provided config with default values
        this._config = Object.assign({
            component_class: '',
            form_class: '',
            is_logged_in: false,
            is_testing: false,
            munchkin_id: '',
            segment_id: '',
            user_id: '',
        }, user_config);
        this._config.site = new URL(window.location.href);
        // Copy relevant fields to User object
        this.user.client_id = client_id;
        this.user.is_logged_in = this._config.is_logged_in;
        this.user.munchkin_id = this._config.munchkin_id;
        this.user.segment_id = this._config.segment_id;
        this.user.id = this._config.user_id;
        // Define page
        this.page = {
            author: document.querySelector('meta[name="author"]') ? document.querySelector('meta[name="author"]').content : '',
            category: '',
            section: '',
            template: [...document.body.classList].filter(classname => classname.startsWith('page-template') || classname.startsWith('single')).join(' '),
            title: document.title || '',
            published_date: '',
            url: this._config.site.href
        };
        // Get published date
        if (document.querySelector('meta[property="article:published_time"]')) {
            this.page.published_date = document.querySelector('meta[property="article:published_time"]').content;
        }
        else if (document.querySelector('meta[property="article:modified_time"]')) {
            this.page.published_date = document.querySelector('meta[property="article:modified_time"]').content;
        }
        else if (document.querySelector('meta[itemprop="datePublished"]')) {
            this.page.published_date = document.querySelector('meta[itemprop="datePublished"]').content;
        }
    }
    /**
     * Init global pageview
     *
     * @param object dataLayer
     */
    global(dataLayer) {
        window.dataLayer.push(this.helpers.process_datalayer(dataLayer));
    }
    /**
     * Click events
     *
     * @param   string        location
     * @param   string        classname
     * @param   object        dataLayer
     * @param   Array<string> ignore
     */
    click(css_selector, dataLayer, ignore) {
        const click = new Click(this._config);
        click.any(css_selector, dataLayer, ignore);
    }
    /**
     * YouTube
     *
     * @param   string  event
     * @param   object  dataLayer
     */
    youtube(event, dataLayer) {
        const youtube = new YouTube(this._config);
        switch (event) {
            case 'start':
                youtube.start(dataLayer);
                break;
            case 'end':
                youtube.end(dataLayer);
                break;
        }
    }
    /**
     * Wistia
     *
     * @param   string  event
     * @param   object  dataLayer
     */
    wistia(event, video_id, args, dataLayer) {
        const wistia = new Wistia(this._config);
        wistia.init(video_id);
        switch (event) {
            case 'start':
                wistia.start(dataLayer);
                break;
            case 'progress':
                if ('object' === typeof args && Array.isArray(args.progress)) {
                    wistia.progress(args.progress, dataLayer);
                }
                else {
                    console.warn('RDDL: An object of the form {progress: []} must be passed when sending the progress event.');
                }
                break;
            case 'end':
                wistia.end(dataLayer);
                break;
        }
    }
    /**
     * Form
     *
     * @param   string  event
     * @param   object  dataLayer
     * @param   string  form_class
     */
    form(event, dataLayer, form_class) {
        if ((this._config.form_class === undefined || this._config.form_class == '') && (form_class === undefined || form_class == '')) {
            console.warn('RDDL: form_class parameter is required to use form methods');
            return;
        }
        let formConfig = this._config;
        if (form_class !== undefined && form_class != '')
            formConfig.form_class = form_class;
        const form = new Form(this._config);
        switch (event) {
            case 'start':
                form.start(dataLayer);
                break;
            case 'submit':
                form.submit(dataLayer);
                break;
            case 'exit':
                form.exit(dataLayer);
                break;
        }
    }
    /**
     * Marketo
     *
     * @param   string  event
     * @param   object  dataLayer
     */
    marketo(event, dataLayer, onEvent) {
        if (!window.MktoForms2)
            return;
        let formConfig = this._config;
        const marketo = new Marketo(formConfig);
        switch (event) {
            case 'start':
                marketo.start(dataLayer, onEvent);
                break;
            case 'submit':
                marketo.submit(dataLayer);
                break;
            case 'exit':
                marketo.exit(dataLayer);
                break;
        }
    }
}
window.rddl = new RDDL;
